"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, PhoneCall, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  title: "Direct Support Across UK & Ireland",
  description: "Rapid technical response from qualified application engineers."
}, {
  title: "Regulated Compliance Assurance",
  description: "Full qualification protocols tailored to pharmaceutical standards."
}, {
  title: "Complete Lifecycle Partnership",
  description: "Preventative maintenance, re-qualification, and operator training."
}];
export default function CallToAction() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="calltoaction" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="relative overflow-hidden rounded-2xl border border-border bg-secondary/40 px-6 py-12 md:px-12 md:py-16 lg:px-16">
          {/* Subtle Decorative Accents */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-24 -right-24 size-80 rounded-full bg-primary/10 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-24 size-80 rounded-full bg-primary/5 blur-3xl" />

          <div className="relative mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground mb-6 shadow-2xs">
              <ShieldCheck className="size-3.5 text-primary" />
              <span>Regulated Laboratory &amp; Manufacturing Support</span>
            </div>

            <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              Ready to enhance your analytical workflow?
            </h2>

            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              Speak with our application specialists to discuss instrumentation, qualification
              services, and lifecycle support tailored to your facility in Ireland and the UK.
            </p>

            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Speak with a Specialist</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/SolutionsPage" className="inline-flex items-center gap-2">
                  <span>Explore Solutions</span>
                </Link>
              </Button>
            </div>
          </div>

          <div className="relative mt-12 grid grid-cols-1 gap-6 border-t border-border/80 pt-10 sm:grid-cols-3">
            {features.map((feature, i) => <div key={feature.title} data-index={i} className="flex items-start gap-3 rounded-lg bg-background/60 p-4 border border-border/50">
                <CheckCircle2 className="size-5 shrink-0 text-primary mt-0.5" />
                <div>
                  <h3 className="font-default text-sm font-semibold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}